// Combo page (07 Interactive Prototype "COMBO PDP"; sections/ns-combo.liquid): ticking an item off or on recomputes the
// selected count, the combo tier, the totals, the gift line and the Add combo button from the combo's tiers. The theme
// never changes a price: the owner's automatic discount gives the tier at checkout, and the add itself is the cart's
// (ns-cart.js takes over the /cart/add form, which posts every ticked item in one request). Every word is written with
// textContent, from templates the section prints in data attributes.

export const fill = (template, values) => String(template ?? '').replace(/\{(\w+)\}/g, (match, key) => (key in values ? String(values[key]) : match));

// The combo's tiers field: [{ "items": 2, "percent": 5 }, …]. A tier needs a whole item count of 2 or more and a whole
// percent from 1 to 99, and more items than the tier before it; four at most. The section prints the tiers it kept by the
// same rule, so the page and the script agree on them.
export function parseTiers(input) {
  let list = input;
  if (typeof list === 'string') {
    try { list = JSON.parse(list); } catch { return []; }
  }
  if (!Array.isArray(list)) return [];
  const tiers = [];
  for (const tier of list) {
    if (tiers.length >= 4) break;
    const items = tier?.items;
    const percent = tier?.percent;
    if (!Number.isInteger(items) || items < 2 || !Number.isInteger(percent) || percent < 1 || percent > 99) continue;
    if (tiers.length && items <= tiers.at(-1).items) continue;
    tiers.push({ items, percent });
  }
  return tiers;
}

export const tierFor = (tiers, count) => tiers.filter(tier => tier.items <= count).at(-1) ?? null;
export const nextTier = (tiers, count) => tiers.find(tier => tier.items > count) ?? null;

// The list sum of the ticked prices (cents), the tier percent off it rounded half up to the cent, as the section's Liquid does.
export function comboTotals(prices, tiers) {
  const was = prices.reduce((sum, price) => sum + (Number.isFinite(Number(price)) ? Number(price) : 0), 0);
  const percent = tierFor(tiers, prices.length)?.percent ?? 0;
  const off = Math.round(was * percent / 100);
  return { count: prices.length, was, percent, off, now: was - off };
}

export const giftReached = (now, threshold) => Number(threshold) > 0 && now >= Number(threshold);

// Like Shopify's money_without_trailing_zeros: $699, $481.16.
export function formatMoney(cents, currency) {
  const value = Number(cents);
  if (!Number.isFinite(value)) return '';
  const whole = value % 100 === 0;
  const format = code => new Intl.NumberFormat('en-US', { style: 'currency', currency: code, minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 }).format(value / 100);
  try { return format(currency || 'USD'); } catch { return format('USD'); }
}

// Everything the page shows for a selection: { prices } of the ticked items, the number of items that can be ticked
// (total), the tiers, the gift threshold in cents (0: no gift) and the currency.
export function view({ prices, total, tiers, threshold, currency }, strings) {
  const totals = comboTotals(prices, tiers);
  const money = cents => formatMoney(cents, currency);
  const { count, percent, off, now } = totals;
  const tier = tierFor(tiers, count);
  const next = nextTier(tiers, count);
  let tierLine = '';
  if (tier) tierLine = fill(strings.tierLine, { count, percent });
  else if (next) tierLine = next.items - count === 1 ? fill(strings.tierNextOne, { percent: next.percent }) : fill(strings.tierNext, { count: next.items - count, percent: next.percent });
  let add = fill(strings.addMany, { count, price: money(now) });
  if (count === 0) add = strings.addNone;
  else if (tiers.length) add = fill(strings.add, { price: money(now) });
  else if (count === 1) add = fill(strings.addOne, { price: money(now) });
  return {
    count: fill(strings.count, { count, total }),
    was: money(totals.was),
    now: money(now),
    off: off > 0 ? `−${money(off)}` : '',
    tierLine,
    current: tier ? tier.items : 0,
    save: off > 0 ? fill(strings.save, { amount: money(off) }) : '',
    hot: percent > 0 ? fill(strings.hot, { percent }) : '',
    gift: giftReached(now, threshold),
    add,
    addDisabled: count === 0,
  };
}

// ---- Browser wiring ----

const stringsOf = root => ({
  count: root.dataset.tCount, tierLine: root.dataset.tTierLine, tierNextOne: root.dataset.tTierNextOne, tierNext: root.dataset.tTierNext,
  save: root.dataset.tSave, hot: root.dataset.tHot, add: root.dataset.tAdd, addOne: root.dataset.tAddOne, addMany: root.dataset.tAddMany, addNone: root.dataset.tAddNone,
});

function update(root) {
  const boxes = [...root.querySelectorAll('input[data-ns-combo-check]')];
  const prices = boxes.filter(box => box.checked).map(box => Number(box.dataset.price) || 0);
  const next = view({ prices, total: boxes.length, tiers: parseTiers(root.dataset.tiers), threshold: Number(root.dataset.giftThreshold) || 0, currency: root.dataset.currency }, stringsOf(root));
  const write = (selector, value, hideEmpty = false) => {
    for (const node of root.querySelectorAll(selector)) {
      node.textContent = value;
      if (hideEmpty) node.hidden = value === '';
    }
  };
  write('[data-ns-combo-count]', next.count);
  write('[data-ns-combo-was]', next.was);
  write('[data-ns-combo-now]', next.now);
  write('[data-ns-combo-off]', next.off, true);
  write('[data-ns-combo-tier-line]', next.tierLine);
  write('[data-ns-combo-save]', next.save, true);
  write('[data-ns-combo-hot]', next.hot, true);
  for (const node of root.querySelectorAll('[data-ns-combo-saving]')) node.hidden = next.save === '';
  for (const node of root.querySelectorAll('[data-ns-combo-gift-row]')) node.hidden = !next.gift;
  for (const input of root.querySelectorAll('input[data-ns-combo-gift]')) input.disabled = !next.gift;
  for (const cell of root.querySelectorAll('[data-ns-combo-tier]')) {
    const current = Number(cell.dataset.items) === next.current;
    cell.classList.toggle('is-current', current);
    if (current) cell.setAttribute('aria-current', 'true');
    else cell.removeAttribute('aria-current');
    const you = cell.querySelector('[data-ns-combo-you]');
    if (you) you.hidden = !current;
  }
  for (const button of root.querySelectorAll('[data-ns-combo-add]')) {
    button.textContent = next.add;
    button.disabled = next.addDisabled;
  }
}

if (typeof document !== 'undefined') {
  document.addEventListener('change', event => {
    const box = event.target instanceof Element ? event.target.closest('input[data-ns-combo-check]') : null;
    const root = box?.closest('[data-ns-combo-page]');
    if (root) update(root);
  });
  // A page restored from the back-forward cache keeps its ticks; the texts follow them.
  const start = () => { for (const root of document.querySelectorAll('[data-ns-combo-page]')) update(root); };
  window.addEventListener('pageshow', start);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
}
